import React, { useEffect, useRef } from 'react'
import { Toast } from 'primereact/toast'
import { useTranslation } from 'react-i18next'
import { apiGetInfo } from '@ms/shared/services/api/user'

import mainIcon from '../../assets/images/main_icon.png'
import backgroundImage from '../../assets/images/new_bg_login.png'
import AuthHeaderActions from './auth_header_actions'
import {
	clearAuthSession,
	getAuthSession,
	isAuthSessionExpired,
} from '../../services/auth_storage'
import {
	clearOnboardingSession,
	setOnboardingSession,
} from '../../services/onboarding_storage'
import { redirectToSocial } from '../../services/social_handoff'
import './style/main_login.css'

const accountTypes = [
	{
		key: 'student',
		roleId: 2,
		titleKey: 'mainLogin.accounts.student.title',
		descriptionKey: 'mainLogin.accounts.student.description',
		icon: 'pi pi-graduation-cap',
	},
	{
		key: 'teacher',
		roleId: 3,
		titleKey: 'mainLogin.accounts.teacher.title',
		descriptionKey: 'mainLogin.accounts.teacher.description',
		icon: 'pi pi-id-card',
	},
	{
		key: 'guest',
		roleId: 1,
		titleKey: 'mainLogin.accounts.guest.title',
		descriptionKey: 'mainLogin.accounts.guest.description',
		icon: 'pi pi-user',
	},
]

const benefits = [
	{
		icon: 'pi pi-book',
		titleKey: 'mainLogin.benefits.flexible.title',
		descriptionKey: 'mainLogin.benefits.flexible.description',
		color: 'green',
	},
	{
		icon: 'pi pi-map-marker',
		titleKey: 'mainLogin.benefits.anywhere.title',
		descriptionKey: 'mainLogin.benefits.anywhere.description',
		color: 'blue',
	},
	{
		icon: 'pi pi-chart-line',
		titleKey: 'mainLogin.benefits.quality.title',
		descriptionKey: 'mainLogin.benefits.quality.description',
		color: 'purple',
	},
	{
		icon: 'pi pi-users',
		titleKey: 'mainLogin.benefits.community.title',
		descriptionKey: 'mainLogin.benefits.community.description',
		color: 'yellow',
	},
]

function LoginButton({ compact = false, showDescription = false }) {
	const { t } = useTranslation()
	const handleLogin = () => {
		const loginUrl = new URL(
			`${process.env.REACT_APP_MAIN_ENDPOINT}/auth/login_google`,
			window.location.origin,
		)

		window.location.assign(loginUrl.toString())
	}

	return (
		<button
			type='button'
			className={`main-login__login-button${compact ? ' main-login__login-button--compact' : ''}`}
			onClick={handleLogin}
			aria-label={t('mainLogin.login.ariaLabel')}
		>
			<span className='main-login__login-icon'><i className='pi pi-lock' /></span>
			<span className='main-login__login-copy'>
				<span className='main-login__login-title'>
					<strong>{t('mainLogin.login.title')}</strong>
					<small>{t('mainLogin.login.existingUser')}</small>
				</span>
				{showDescription && <small className='main-login__login-description'>{t('mainLogin.login.description')}</small>}
			</span>
			<i className='pi pi-chevron-right' aria-hidden='true' />
		</button>
	)
}

function RegisterPanel() {
	const { t } = useTranslation()
	const handleRegister = (account) => {
		const loginUrl = new URL(
			`${process.env.REACT_APP_MAIN_ENDPOINT}/auth/login_google`,
			window.location.origin,
		)

		if (account.roleId) {
			loginUrl.searchParams.set('role_id', account.roleId)
		}

		window.location.assign(loginUrl.toString())
	}

	return (
		<section className='main-login__register-panel' aria-labelledby='main-login-register-heading'>
			<div className='main-login__mobile-login'><LoginButton showDescription /></div>

			<div className='main-login__panel-heading'>
				<span className='main-login__register-heading-icon'><i className='pi pi-users' /></span>
				<div className='main-login__ornament' aria-hidden='true'><span /></div>
				<div>
					<h2 id='main-login-register-heading'>{t('mainLogin.register.title')}</h2>
					<p>{t('mainLogin.register.subtitle')}</p>
				</div>
				<div className='main-login__ornament main-login__ornament--right' aria-hidden='true'><span /></div>
			</div>

			<div className='main-login__account-grid'>
				{accountTypes.map((account) => (
					<button
						key={account.key}
						type='button'
						className={`main-login__account-card main-login__account-card--${account.key}`}
						onClick={() => handleRegister(account)}
						aria-label={t('mainLogin.register.accountAriaLabel', { type: t(account.titleKey) })}
					>
						<span className='main-login__account-icon'><i className={account.icon} /></span>
						<span className='main-login__account-copy'>
							<strong>{t(account.titleKey)}</strong>
							<span>{t(account.descriptionKey)}</span>
						</span>
						<span className='main-login__account-arrow' aria-hidden='true'>
							<i className='pi pi-chevron-right' />
						</span>
					</button>
				))}
			</div>
		</section>
	)
}

function Benefits() {
	const { t } = useTranslation()
	return (
		<div className='main-login__benefits'>
			{benefits.map((benefit) => (
				<div className='main-login__benefit' key={benefit.titleKey}>
					<span className={`main-login__benefit-icon main-login__benefit-icon--${benefit.color}`}>
						<i className={benefit.icon} />
					</span>
					<span className='main-login__benefit-copy'>
						<strong>{t(benefit.titleKey)}</strong>
						<small>{t(benefit.descriptionKey)}</small>
					</span>
				</div>
			))}
		</div>
	)
}

export default function MainLogin() {
	const { t } = useTranslation()
	const toast = useRef(null)

	useEffect(() => {
		const params = new URLSearchParams(window.location.search)

		if (params.get('logout') === 'true') {
			sessionStorage.removeItem('ms2_mobile_auth_state')
			clearAuthSession()
			if (params.get('application_cancelled') === 'true') {
				toast.current?.show({
					severity: 'success',
					summary: t('mainLogin.cancelSuccess.summary'),
					detail: t('mainLogin.cancelSuccess.detail'),
					life: 5000,
				})
			}
			window.history.replaceState({}, document.title, window.location.pathname)
			return
		}

		const mobileState = params.get('mobile_auth')
		if (mobileState && /^[a-f0-9]{64}$/.test(mobileState)) {
			sessionStorage.setItem('ms2_mobile_auth_state', mobileState)
			window.history.replaceState({}, document.title, window.location.pathname)
			if (!getAuthSession().token || isAuthSessionExpired()) {
				window.location.assign(`${process.env.REACT_APP_MAIN_ENDPOINT}/auth/login_google`)
				return
			}
		} else {
			sessionStorage.removeItem('ms2_mobile_auth_state')
		}

		const { token } = getAuthSession()
		if (!token) return

		if (isAuthSessionExpired()) {
			clearAuthSession()
			return
		}

		let active = true
		const continueLogin = async () => {
			const userInfoResponse = await apiGetInfo()
			const userInfo = userInfoResponse?.data

			if (!userInfo || typeof userInfo !== 'object') {
				throw new Error(userInfoResponse?.message || 'Unable to load user information')
			}

			if (!active) return

			const roleId = Number(userInfo.role_id)
			const shouldContinueOnboarding = (
				userInfo.onboarding_completed_at === null
				&& Array.isArray(userInfo.platform_users)
				&& userInfo.platform_users.length === 0
			)

			if (shouldContinueOnboarding) {
				const onboardingRoutes = {
					2: '/google_login/student_onboarding',
					3: '/google_login/teacher_onboarding',
				}
				const onboardingRoute = onboardingRoutes[roleId]

				if (!onboardingRoute) {
					throw new Error('This user type does not have an onboarding page')
				}

				setOnboardingSession({
					data: userInfo.onboarding_data ?? {},
					roleId,
				})
				window.location.replace(onboardingRoute)
				return
			}

			clearOnboardingSession()
			await redirectToSocial()
		}

		continueLogin().catch((error) => {
			console.error('Error continuing login:', error)
		})

		return () => {
			active = false
		}
	}, [t])

	return (
		<main className='main-login' style={{ '--main-login-bg': `url(${backgroundImage})` }}>
			<Toast ref={toast} position='top-center' />
			<header className='main-login__header'>
				<div className='main-login__header-actions'>
					<AuthHeaderActions />
					<LoginButton compact />
				</div>
			</header>

			<section className='main-login__layout'>
				<div className='main-login__hero'>
					<div className='main-login__headline'>
						<h1>{t('mainLogin.hero.education')} <span>{t('mainLogin.hero.flexible')}</span></h1>
						<h2>{t('mainLogin.hero.subtitle')}</h2>
						<p className='main-login__lead'>
							{t('mainLogin.hero.learnAnywhere')} <span>•</span> {t('mainLogin.hero.discoverYourself')} <span>•</span> {t('mainLogin.hero.planLearning')} <span>•</span> {t('mainLogin.hero.unlimitedPotential')}
						</p>
						<p className='main-login__intro'>
							{t('mainLogin.hero.introLine1')}<br />
							{t('mainLogin.hero.introLine2')}
						</p>
					</div>

					<div className='main-login__mascot-frame'>
						<img src={mainIcon} alt={t('mainLogin.mascotAlt')} />
					</div>
				</div>

				<div className='main-login__panel-column'>
					<RegisterPanel />
				</div>
			</section>

			<div className='main-login__desktop-benefits'><Benefits /></div>
			<div className='main-login__mobile-benefits'><Benefits /></div>

			<footer className='main-login__footer'>
				<strong>{t('mainLogin.footer.title')}</strong>
				<span>{t('mainLogin.footer.subtitle')}</span>
			</footer>
		</main>
	)
}
